import React from 'react';
import styled from '@emotion/styled';
import { Tooltip, useTheme } from '@mui/material';

interface StripedProgressBarProps {
  height?: number;
  total: number;
  pending: number;
  completed: number;
  error: number;
  cancelled: number;
  tooltipText?: string;
  isProcessing?: boolean;
}

const StripedProgressBar = ({
  height = 8,
  total = 0,
  pending = 0,
  completed = 0,
  error = 0,
  cancelled = 0,
  tooltipText,
  isProcessing = false
}: StripedProgressBarProps) => {
  const theme = useTheme();

  const pendingPercentage = (pending / total) * 100;
  const errorPercentage = (error / total) * 100;
  const completedPercentage = (completed / total) * 100;
  const cancelledPercentage = (cancelled / total) * 100;
  const totalPercentage = 100 - pendingPercentage - completedPercentage - errorPercentage - cancelledPercentage;

  const values = [
    {
      percentage: `${completedPercentage}%`,
      color: theme.palette.success.main
    },
    {
      percentage: `${pendingPercentage}%`,
      color: theme.palette.warning.main
    },
    {
      percentage: `${errorPercentage}%`,
      color: theme.palette.error.main
    },
    {
      percentage: `${cancelledPercentage}%`,
      color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]
    }
  ];

  const renderProgressLine = () => (
    <Bar height={height}>
      {!isProcessing ? (
        <>
          <ValueWrapper width={isNaN(totalPercentage) ? 0 : totalPercentage}>
            {values.map((value, index) => {
              const offset = index === 0 ? 0 : values.slice(0, index).reduce((acc, cur) => acc + parseFloat(cur.percentage), 0);
              return <Value key={index} color={value.color} width={parseFloat(value.percentage) || 0} offset={isNaN(offset) ? 0 : offset} />;
            })}
          </ValueWrapper>
        </>
      ) : (
        <Progress height={height}>
          <div />
        </Progress>
      )}
    </Bar>
  );

  const renderTooltip = () => <Tooltip title={tooltipText}>{renderProgressLine()}</Tooltip>;

  return tooltipText ? renderTooltip() : renderProgressLine();
};

export default StripedProgressBar;

const Bar = styled.div<{ height: number }>`
  position: relative;
  width: 100%;
  height: 8px;
  margin: 20px 0;
  border-radius: ${({ height }) => height / 2}px;
  overflow: hidden;
`;

const ValueWrapper = styled.div<{ width: number }>`
  width: 100%;
  height: 100%;
  background: transparent;
  position: absolute;
  z-index: 2;
  transition: width 1s;
  display: flex;
  flex-direction: row;
`;

const Value = styled.div<{ width: number; color: string; offset: number }>`
  height: 100%;
  transition: width 1s;
  width: ${({ width }) => width}%;
  background: ${({ theme, color }) => color};
  box-shadow: 3px 0 3px rgba(0, 0, 0, 0.5);
`;

const Progress = styled.div<{ height: number }>`
  position: 
  display: block;
  overflow: hidden;
  height: ${({ height }) => height}px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);

  > div {
    height: 100%;
    box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.15);
    background-image: ${({ theme }) => {
      const baseColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400];
      const lightColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300];
      return `linear-gradient(-45deg, ${baseColor} 25%, ${lightColor} 0, ${lightColor} 50%, ${baseColor} 0, ${baseColor} 75%, ${lightColor} 0, ${lightColor})`;
    }};
    background-size: ${({ height }) => `${height * 2}px ${height * 2}px`};

    -webkit-animation: f 2s linear infinite;
    animation: f 2s linear infinite;

    @-webkit-keyframes f {
      0% {
        background-position: -2px 0;
      }
      100% {
        background-position: 30px 0; 
    }

    @keyframes f {
      0% {
        background-position: -2px 0; 
      }
      100% {
        background-position: 30px 0; 
      }
    }
  }
`;
